import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { ClipboardX, Plus } from "lucide-react";
import PropTypes from "prop-types";

import { TabSetsLevels, UIButton, UIEmptyState, UIPaginationBar } from "../../../ui";
import messages from "../../../../assets/locale/messages";
import { companyPermissionModules, eventStatuses } from "../../../../utils/Constants";
import {
  canAddCompanyModule,
  canEditCompanyModule,
  isCompanyOwner,
  isSupervisorManger,
} from "../../../../utils/Permissions";
import { isPortalEventCancelled } from "../../../../utils/companyPortal/eventEditRules";
import EvaluationRoundsFilters from "./EvaluationRoundsFilters";
import EvaluationRoundsTable from "./EvaluationRoundsTable";
import EvaluationRoundApplicants from "./EvaluationRoundApplicants";
import StartEvaluationRoundModal from "./StartEvaluationRoundModal";
import EventSatisfactionSurveyView from "./EventSatisfactionSurveyView";
import useEvaluationRounds from "./useEvaluationRounds";
import { EVALUATION_SUB_TABS } from "./constants";

import "./_Evaluations.scss";

const EvaluationRounds = ({
  event,
  selectedRound: selectedRoundProp,
  onRoundSelect,
  readOnly,
  portalVariant,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const t = messages[lang].events.viewEvents.evaluationsTab;
  const isSupervisor = isSupervisorManger();

  // Evaluations are owned by the Company Portal; the admin variant is read-only,
  // except for the event Supervisor, who may submit (but never start a round).
  const isCompany = portalVariant === "company-portal" && !readOnly;
  const canAddRound =
    isCompany && canAddCompanyModule(companyPermissionModules.evaluations, login);
  // A cancelled event's evaluations stay as they are, for everyone.
  const canSubmit =
    !isPortalEventCancelled(event?.status) &&
    (isSupervisor ||
      (isCompany && canEditCompanyModule(companyPermissionModules.evaluations, login)));
  // Company admins (owners) may submit for any applicant; project managers
  // are restricted to supervisor-role forms only (enforced in
  // ApplicantEvaluationsTable). The event Supervisor evaluates every
  // applicant in their zone, so they are not subject to that restriction.
  const canSubmitAll = isSupervisor || (canSubmit && isCompanyOwner(login));

  const [activeSubTab, setActiveSubTab] = useState(EVALUATION_SUB_TABS.applicants);
  const [selectedRoundLocal, setSelectedRoundLocal] = useState(null);
  const [isStartRoundModalOpen, setIsStartRoundModalOpen] = useState(false);

  // When onRoundSelect is provided (company portal), state is controlled by the parent.
  // Otherwise fall back to local state (admin portal).
  const isControlled = onRoundSelect !== undefined;
  const selectedRound = isControlled ? selectedRoundProp : selectedRoundLocal;
  const setSelectedRound = isControlled ? onRoundSelect : setSelectedRoundLocal;

  const {
    search,
    filters,
    page,
    pageSize,
    rows,
    total,
    pages,
    rangeFrom,
    rangeTo,
    hasAnyRounds,
    runningRound,
    allCompleted,
    statusOptions,
    startRound,
    handleSearchChange,
    handleFilterChange,
    handlePageChange,
    handlePageSizeChange,
  } = useEvaluationRounds(event?.id, portalVariant);

  // Only ongoing events may start a new evaluation round
  const isOngoingEvent = event?.status === eventStatuses.ongoing;
  const canStartRound = canAddRound && isOngoingEvent;

  const handleStartRoundConfirm = () => {
    startRound();
    setIsStartRoundModalOpen(false);
  };

  // Supervisors use user_app tokens; company satisfaction survey is admin/company
  // only — showing the tab would 401 and log them out.
  const subTabs = [
    { value: EVALUATION_SUB_TABS.applicants, label: t.subTabs.applicants },
    !isSupervisor && {
      value: EVALUATION_SUB_TABS.company,
      label: t.subTabs.company,
    },
  ].filter(Boolean);

  const renderIndicator = () => {
    if (runningRound) {
      return (
        <Box className="eval-rounds__running-badge">
          <span className="eval-rounds__running-dot" />
          <Typography variant="body2" className="eval-rounds__running-label">
            {t.runningIndicator.replace("{name}", runningRound.name)}
          </Typography>
        </Box>
      );
    }
    if (allCompleted && canStartRound) {
      return (
        <UIButton
          variant="primary"
          appearance="outline"
          size="large"
          type="button"
          className="eval-rounds__start-btn"
          onClick={() => setIsStartRoundModalOpen(true)}
        >
          {t.startEvaluationRound}
        </UIButton>
      );
    }
    return null;
  };

  const isApplicantsTab = activeSubTab === EVALUATION_SUB_TABS.applicants;
  const isFiltered = Boolean(
    search.trim() || filters.status || filters.startDate || filters.endDate,
  );

  return (
    <Box className="eval-rounds">
      <TabSetsLevels
        tabs={subTabs}
        value={activeSubTab}
        onChange={(tab) => {
          setActiveSubTab(tab);
          setSelectedRoundLocal(null);
          onRoundSelect?.(null);
        }}
      />

      {isApplicantsTab ? (
        <>
          {!hasAnyRounds ? (
            <UIEmptyState
              icon={<ClipboardX size={40} />}
              title={t.companyTab.emptyTitle}
              description={t.companyTab.emptyDescription}
              actionLabel={canStartRound ? t.startEvaluationRound : undefined}
              actionIcon={canStartRound ? <Plus size={16} /> : undefined}
              onAction={
                canStartRound ? () => setIsStartRoundModalOpen(true) : undefined
              }
            />
          ) : selectedRound ? (
            <EvaluationRoundApplicants
              round={selectedRound}
              event={event}
              readOnly={readOnly}
              canSubmit={canSubmit}
              canSubmitAll={canSubmitAll}
              portalVariant={portalVariant}
            />
          ) : (
            <>
              <EvaluationRoundsFilters
                copy={t}
                search={search}
                filters={filters}
                statusOptions={statusOptions}
                renderActionBeforeFilterToggle={renderIndicator()}
                onSearchChange={handleSearchChange}
                onFilterChange={handleFilterChange}
              />

              <EvaluationRoundsTable
                rows={rows}
                labels={t.table}
                onRowClick={(round) => setSelectedRound(round)}
                isFiltered={isFiltered}
              />

              <UIPaginationBar
                currentPage={page}
                pages={pages}
                pageSize={pageSize}
                rangeTemplate={t.rangeTemplate}
                rangeFrom={rangeFrom}
                rangeTo={rangeTo}
                rangeTotal={total}
                rowsPerPageLabel={t.rowsPerPageLabel}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </>
      ) : (
        <EventSatisfactionSurveyView event={event} portalVariant={portalVariant} />
      )}

      <StartEvaluationRoundModal
        open={isStartRoundModalOpen}
        copy={t.startRoundModal}
        onClose={() => setIsStartRoundModalOpen(false)}
        onConfirm={handleStartRoundConfirm}
      />
    </Box>
  );
};

EvaluationRounds.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
  }),
  selectedRound: PropTypes.object,
  onRoundSelect: PropTypes.func,
  readOnly: PropTypes.bool,
  portalVariant: PropTypes.oneOf(["company-portal", "admin-portal"]),
};

EvaluationRounds.defaultProps = {
  event: null,
  selectedRound: null,
  onRoundSelect: undefined,
  readOnly: false,
  portalVariant: "company-portal",
};

export default EvaluationRounds;
